import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Eye, EyeOff, Users, ArrowRight } from "lucide-react";

export default function AdminPrograms() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/admin/programs");
      setPrograms(data);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/admin/programs", {
        slug: newTitle,
        title: newTitle.trim(),
        price_eur: 49,
        duration_weeks: 4,
        weeks: [{ week_number: 1, title: "Semana 1", days: [] }],
        is_published: false,
      });
      toast.success("Programa creado.");
      navigate(`/app/admin/programas/${data.id}`);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setCreating(false);
    }
  };

  const togglePublish = async (p) => {
    try {
      await api.put(`/admin/programs/${p.id}`, { is_published: !p.is_published });
      toast.success(!p.is_published ? "Publicado." : "Guardado como borrador.");
      load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Eliminar "${p.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/programs/${p.id}`);
      toast.success("Programa eliminado.");
      load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <AppShell>
      <div className="p-5 sm:p-8" data-testid="admin-programs">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="qv-overline">CATÁLOGO</div>
            <h1 className="qv-display text-white text-4xl sm:text-5xl mt-2">Programas</h1>
            <p className="mt-3 text-neutral-400 text-sm">Contenido evergreen que se vende solo.</p>
          </div>
        </div>

        {/* Quick create */}
        <form onSubmit={handleCreate} className="mt-8 border border-white/10 bg-[#0a0a0a] p-5 flex flex-col sm:flex-row gap-3">
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Nombre del programa (ej. Pretemporada 4 Semanas)"
            className="qv-input flex-1"
            data-testid="admin-program-new-title"
          />
          <button type="submit" disabled={creating} className="qv-btn-primary justify-center disabled:opacity-60" data-testid="admin-program-create">
            <Plus size={16} /> {creating ? "CREANDO..." : "CREAR"}
          </button>
        </form>

        {/* List */}
        <div className="mt-8">
          {loading && <div className="text-neutral-500 text-sm">Cargando...</div>}
          {!loading && programs.length === 0 && (
            <div className="border border-white/10 bg-[#0a0a0a] p-10 text-center">
              <div className="qv-overline">SIN PROGRAMAS</div>
              <h2 className="qv-display text-white text-2xl mt-3">Crea tu primer programa.</h2>
              <p className="mt-3 text-neutral-400 text-sm max-w-md mx-auto">
                Un programa es un plan de entrenamiento fijo que cualquier futbolista puede comprar. Tú lo creas una vez.
              </p>
            </div>
          )}
          {!loading && programs.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admin-programs-grid">
              {programs.map((p) => (
                <div key={p.id} className="border border-white/10 bg-[#0a0a0a] p-5 flex flex-col gap-3" data-testid={`admin-program-row-${p.slug}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="qv-display text-white text-xl leading-tight">{p.title}</div>
                      {p.subtitle && <div className="text-neutral-400 text-xs mt-1">{p.subtitle}</div>}
                    </div>
                    <div className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${p.is_published ? "border-[color:var(--qv-wa)]/50 text-[color:var(--qv-wa)]" : "border-white/15 text-neutral-500"}`}>
                      {p.is_published ? "Publicado" : "Borrador"}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 text-[11px] text-neutral-300">
                    <span>{Math.round(p.price_eur)}€</span>
                    <span>· {p.duration_weeks} sem</span>
                    <span>· {p.total_sessions} sesiones</span>
                  </div>
                  <div className="mt-auto pt-3 flex flex-wrap items-center gap-2 border-t border-white/5">
                    <Link to={`/app/admin/programas/${p.id}`} className="qv-btn-ghost text-xs" data-testid={`admin-program-edit-${p.slug}`}>
                      <Edit size={13} /> EDITAR
                    </Link>
                    <button type="button" onClick={() => togglePublish(p)} className="qv-btn-ghost text-xs" data-testid={`admin-program-toggle-${p.slug}`}>
                      {p.is_published ? <><EyeOff size={13} /> DESPUBLICAR</> : <><Eye size={13} /> PUBLICAR</>}
                    </button>
                    <Link to={`/programas/${p.slug}`} target="_blank" rel="noreferrer" className="qv-btn-ghost text-xs">
                      VER <ArrowRight size={13} />
                    </Link>
                    <button type="button" onClick={() => del(p)} className="ml-auto text-neutral-500 hover:text-red-500 transition-colors" aria-label="Eliminar" data-testid={`admin-program-delete-${p.slug}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
